import React from 'react';
import { Hexagon } from 'react-feather';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFacebookF,
    faInstagram,
    faLinkedinIn,
    faTwitter,
    faYoutube,
    faPinterestP,
    faTiktok,
} from '@fortawesome/free-brands-svg-icons';
import { useLocalizedData } from '../hooks/useTranslatedContent';
import { useTranslation } from 'react-i18next';

export default function ContactData({ contactUsData }) {
    const { getLocalized } = useLocalizedData(contactUsData);
    const { t } = useTranslation();

    if (!contactUsData) return null;

    const contactInfo = {
        phone: {
            value: getLocalized('phoneNumber'),
            title: getLocalized('phoneTitle') || t('contact.phoneTitle'),
            icon: <i className="uil uil-phone"></i>
        },
        email: {
            value: getLocalized('contactEmail'),
            title: getLocalized('emailTitle') || t('contact.emailTitle'),
            icon: <i className="uil uil-envelope"></i>
        },
        location: {
            value: getLocalized('address'),
            title: getLocalized('locationTitle') || t('contact.locationTitle'),
            icon: <i className="uil uil-map-marker"></i>
        }
    };

    const socialLinks = {
        facebook: getLocalized('facebook'),
        instagram: getLocalized('instagram'),
        linkedin: getLocalized('linkedin'),
        twitter: getLocalized('twitter'),
        youtube: getLocalized('youtube'),
        pinterest: getLocalized('pinterest'),
        tiktok: getLocalized('tiktok'),
    };

    const availableContacts = Object.entries(contactInfo)
        .filter(([_, info]) => info.value)
        .map(([key, info]) => ({ key, ...info }));

    const socialIcons = [
        { key: 'facebook', icon: faFacebookF },
        { key: 'instagram', icon: faInstagram },
        { key: 'linkedin', icon: faLinkedinIn },
        { key: 'twitter', icon: faTwitter },
        { key: 'youtube', icon: faYoutube },
        { key: 'pinterest', icon: faPinterestP },
        { key: 'tiktok', icon: faTiktok },
    ];

    return (
        <div className="container lg:mt-24 mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-[30px]">
                {availableContacts.map((contact) => (
                    <div className="text-center px-6" key={contact.key}>
                        <div className="relative overflow-hidden text-transparent -m-3">
                            <Hexagon className="h-28 w-28 fill-violet-600/5 mx-auto rotate-[30deg]" />
                            <div className="absolute top-2/4 -translate-y-2/4 start-0 end-0 mx-auto text-black-600 rounded-xl transition-all duration-500 ease-in-out text-4xl flex align-middle justify-center items-center">
                                {contact.icon}
                            </div>
                        </div>

                        <div className="content mt-7">
                            <h5 className="title h5 text-xl font-medium">{contact.title}</h5>
                            <div className="mt-5">
                                {contact.key === 'phone' ? (
                                    <a
                                        href={`tel:${contact.value}`}
                                        className="text-black-600 hover:text-black-600 after:bg-black-600 transition duration-500"
                                    >
                                        {contact.value}
                                    </a>
                                ) : contact.key === 'email' ? (
                                    <a
                                        href={`mailto:${contact.value}`}
                                        className="text-black-600 hover:text-black-600 after:bg-black-600 transition duration-500"
                                    >
                                        {contact.value}
                                    </a>
                                ) : (
                                    <a
                                        href={`https://www.google.com/maps?q=${encodeURIComponent(contact.value)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            >{contact.value}</a>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Social Icons */}
            <div className="mt-16 text-center">
                <h5 className="text-xl font-semibold mb-6">{t('contact.followUs', 'Follow Us')}</h5>

                <ul className="flex flex-wrap justify-center gap-2">
                    {socialIcons.map(({ key, icon, isEmail }) => {
                        const link = socialLinks[key];
                        if (!link) return null;

                        return (

                            <li key={key}>
                                <a
                                    href={isEmail ? `mailto:${link}` : link}
                                    target={isEmail ? '_self' : '_blank'}
                                    rel={isEmail ? '' : 'noopener noreferrer'}
                                    className="h-8 w-8 inline-flex items-center justify-center border border-gray-300 rounded-md hover:border-gray-500 hover:bg-black/10 transition text-gray-800"
                                    title={key.charAt(0).toUpperCase() + key.slice(1)}
                                >
                                    <FontAwesomeIcon icon={icon} />
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

